import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { ROUTES } from '@/app/config/constants';
import { AppShell } from '@/app/layout/components/AppShell';
import { ROLES } from '@/core/security/roles';
import { RequireFeature } from '@/features/authentication';
import { purchaseRequestRoutes } from '@/features/purchase-request';
import { masterRoutes } from '@/features/masters';
import { organizationSetupRoutes } from '@/features/organization-setup';
import { rbacRoutes } from '@/features/rbac';
import { rfqRoutes } from '@/features/bids';
import { supplierDashboardRoutes, supplierQuotationRoutes } from '@/features/supplier';
import { templateRoutes } from '@/features/templates';
import { workflowConfigRoute } from '@/features/workflow-configuration';
import { aiControlRoute } from '@/features/ai-control';
import { LazyLoad } from '@/shared/components/LazyLoad';
import { ProtectedRoute } from './ProtectedRoute';
import { BuyerDashboardGate, RoleLanding, RoleShellGuard } from './RoleLanding';

const DashboardPage = lazy(() => import('@/app/layout/pages/DashboardPage'));
const ProfilePage = lazy(() => import('@/app/layout/pages/ProfilePage'));
const SettingsPage = lazy(() => import('@/app/layout/pages/SettingsPage'));
const NotFoundPage = lazy(() => import('@/app/layout/pages/NotFoundPage'));
const PlaceholderPage = lazy(() => import('@/app/layout/pages/PlaceholderPage'));

function placeholder(title: string): RouteObject['element'] {
  return (
    <LazyLoad>
      <PlaceholderPage title={title} />
    </LazyLoad>
  );
}

export const protectedRoutes: RouteObject[] = [
  {
    path: ROUTES.ROOT,
    element: <ProtectedRoute />,
    children: [
      {
        /*
          The staff shell, or a supplier's own for a session signed in to the Supplier Bid Quotation portal
          (2026-10-23) -- see AppShell. A user's session renders `Layout` exactly as before.
        */
        element: <AppShell />,
        children: [
          {
            /*
              Pathless guard over the whole shell: a Mega Admin is held on
              Organization Setup, whichever URL is typed. Placing it here
              rather than on each route is also what keeps DashboardPage from
              mounting -- so /dashboard/summary is never requested and its
              chunk is never fetched.
            */
            element: <RoleShellGuard />,
            children: [
            /*
              Redirect rather than rendering DashboardPage at both "/" and
              "/dashboard": at "/" the Dashboard NavLink (to="/dashboard") would
              render inactive, so the sidebar would look like nothing is selected.

              RoleLanding picks the destination, because Mega Admin starts on
              Organization Setup rather than the dashboard.
            */
            { index: true, element: <RoleLanding /> },

            {
              /*
                The staff dashboard. A Supplier-role session is sent to its Supplier Dashboard (NB-637) before
                DashboardPage mounts, so it never asks for the staff summary; every other role is unchanged.
              */
              path: 'dashboard',
              element: <BuyerDashboardGate />,
              children: [
                {
                  index: true,
                  element: (
                    <LazyLoad>
                      <DashboardPage />
                    </LazyLoad>
                  ),
                },
              ],
            },
            /* The Supplier Dashboard (`/supplier-dashboard`, 2026-10-29): the Supplier role's home. */
            ...supplierDashboardRoutes,
            {
              path: 'profile',
              element: (
                <LazyLoad>
                  <ProfilePage />
                </LazyLoad>
              ),
            },
            {
              path: 'settings',
              element: (
                <LazyLoad>
                  <SettingsPage />
                </LazyLoad>
              ),
            },

            /*
              The FUNCTION features, guarded by the feature each one leads to --
              the same grants the sidebar filters on, so a hidden menu is also a
              closed URL rather than merely an unlisted one. These three had no
              guard at all before; nothing that could reach them has lost access.

              The BID (RFQ) screens -- list, template picker, wizard and detail -- come
              from the feature's own route table, each guarded by 'Request For
              Quotation' there, the arrangement templateRoutes and masterRoutes use.
            */
            ...rfqRoutes,
            /* The Supplier Bid Quotation (`/supplier-bids`): the Supplier role's BIDs, a feature of its own (2026-10-28). */
            ...supplierQuotationRoutes,
            /*
              Purchase Requests (NB-355): PR Templates filled in for the selected hierarchy -- the list, New Purchase
              Request and one request -- guarded by the same feature, from the forms feature's route table.
            */
            ...purchaseRequestRoutes,
            {
              path: 'reverse-auction',
              element: (
                <RequireFeature feature="Reverse Auction">
                  {placeholder('Reverse Auction')}
                </RequireFeature>
              ),
            },
            /*
              The TEMPLATE features, from the templates feature's own route table.

              This was three `placeholder(...)` elements written out here. Both halves of the merge were
              half right: that block carried the RBAC guards and the grant-aware landing but rendered
              placeholders, while `templateRoutes` rendered the real TemplatesPage and carried neither.
              The guards and the landing have moved into the feature -- the arrangement `masterRoutes`
              already uses -- so the screens are reachable and still guarded by the same grants the
              sidebar filters on.
            */
            ...templateRoutes,

            /*
              RBAC and Workflow Config guard themselves with the FEATURE each
              one leads to, in their own route tables -- `RequireFeature`, the
              arrangement masterRoutes and templateRoutes already use.

              Both sat in a `requiredRoles={[ROLES.ADMIN]}` block here, and both
              carry a `feature` in APP_MENU_ITEMS, so the sidebar and the route
              disagreed the moment an administrator granted either function to
              another role: the entry appeared and the click answered "You do
              not have access to that page". Workflow Config additionally asked
              for `WORKFLOW_VIEW`, a coded permission the platform retired when
              grants became (role, feature, action) -- no session carries it, so
              the route was shut to everybody including Admin.
            */
            ...rbacRoutes,
            workflowConfigRoute,
            aiControlRoute,

            /*
              Organization Setup stays on the role.

              It is the one function deliberately absent from APP_MENU_ITEMS:
              the wizard belongs to the Mega Admin who provisions the tenant and
              MEGA_ADMIN_MENU_ITEMS is its only home, so there is no sidebar
              entry for a grant to disagree with. Guarded, not just hidden --
              without this a non-admin could still reach it by typing the URL.
            */
            {
              element: <ProtectedRoute requiredRoles={[ROLES.ADMIN]} />,
              children: [...organizationSetupRoutes],
            },

            /*
              Masters carries no `requiredRoles`, and that is the point.

              Each of its routes guards itself with the FEATURE it leads to
              (`RequireFeature`, in features/masters/routes.tsx), which is the
              same source the sidebar filters on and the same one the screens
              read for their buttons. Under the old ADMIN block the three
              disagreed: granting an Initiator every permission on a master put
              it in their sidebar and still answered "You do not have access to
              that page" on the click, because the route was asking about the
              role slug instead. Keeping a role guard here would override the
              matrix again and make the grants unenforceable from the screen
              that sets them.

              The refusal itself did not move -- the platform answers 403
              whatever the browser believes.
            */
            ...masterRoutes,

            // Inside Layout, so a bad URL still shows the shell.
            {
              path: '*',
              element: (
                <LazyLoad>
                  <NotFoundPage />
                </LazyLoad>
              ),
            },
            ],
          },
        ],
      },
    ],
  },
];
